/**
 * Sound-effects engine — REAL, tiny audio assets (Kenney CC0, transcoded to
 * mono MP3, ~2–6 KB each, `public/sfx/*.mp3`) played through Howler.
 *
 * Design constraints (Iranian networks + Telegram webviews):
 *  - 100% CLIENT-SIDE: static files served by nginx; the game/api servers are
 *    never involved in producing or timing a sound.
 *  - LAZY: neither Howler (~10 KB gz) nor any clip downloads until the first
 *    user gesture; each clip loads once on first use and is cached forever.
 *  - RELIABLE: Howler owns the mobile-autoplay unlock (iOS/Android queue their
 *    plays until the first gesture), pools WebAudio nodes, and resumes a
 *    suspended context itself — the old hand-rolled WebAudio tones silently
 *    dropped plays whenever the context suspended (screen lock/backgrounding).
 *  - Two user toggles, both persisted per player: GAME sounds (cards, turns,
 *    win/lose) and UI/notification sounds (taps, messages, alerts).
 */

import { getVolume } from "../settings/prefs";

export type SfxName =
  // game
  | "play"
  | "deal"
  | "shuffle"
  | "trickWin"
  | "trickLose"
  | "win"
  | "lose"
  | "heartsBroken"
  | "qspade"
  // شلم's auction (P1 Task 4, R5) — GAME category: a seat's bid, a seat's «پاس», and the «شلم» call.
  // Aliases onto clips already shipped, each trimmed to the card clips' level by MEASUREMENT (see
  // GAIN and public/sfx/LICENSE.txt); `game/shelem-sfx.ts` fires them for the OTHER seats' moves.
  | "bid"
  | "pass"
  | "shelemCall"
  // …and its کف and trump (P1 Task 5, R5): the four کف cards gathered to the حاکم, and the trump
  // named by the حاکم's first card. GAME category; levels by measurement (GAIN, LICENSE.txt).
  | "widowPickup"
  | "trumpCall"
  // …and how its hand ends (P1 Task 6, R5): the contract made, missed (set, یاسا, a failed call), and
  // all twelve tricks (a sweep, a «شلم» call made) — ORIGINAL clips, at the call's event tier.
  | "shelemMade"
  | "shelemSet"
  | "shelemSlam"
  // chess (GAME category — governed by the game-sound toggle + table mute). Bespoke,
  // self-made CC0 **chess** clips (wooden knocks + bell chimes, `chess-*.wav`), one per
  // chess event — NOT the card clips. `chessStart` is the "new game" gong. See
  // `game/chess-sfx.ts`.
  | "chessMove"
  | "chessCapture"
  | "chessCheck"
  | "chessCastle"
  | "chessPromote"
  | "chessStart"
  | "chessIllegal"
  // backgammon (تخته نرد) — GAME category (governed by the game-sound toggle + table mute).
  //
  // EVERY backgammon cue is now backgammon's OWN: dice, chips and wood on a felt board. Nothing
  // here is borrowed from another game or from the interface any more — the bear-off used a
  // Kenney UI confirmation chime, the illegal tap used CHESS's thunk and the mars used the CARD
  // win fanfare. See `game/backgammon-sfx.ts` and public/sfx/LICENSE.txt.
  //
  // There is deliberately NO "it's your turn" chime: on the operator's call (2026-08-15) the
  // notification tone did not belong in تخته نرد — the board already says whose turn it is, and
  // the dice announce the only turn that needs announcing.
  | "bgDiceLand"
  | "bgDoubles"
  | "bgCheckerPlace"
  | "bgHit"
  | "bgBearOff"
  | "bgIllegal"
  | "bgGammon"
  // پاسور (P1 Task 5, R5) — GAME category: a capture by YOUR side (yours or your partner's; the other
  // side's keeps `trickLose`), a سور, a سور that cancels one of the other side's (D10), and a deal's
  // remainder sliding to its last capturer (W16). Aliases onto clips already shipped, each at a level
  // MEASURED against the card clips (GAIN below, public/sfx/LICENSE.txt); `game/use-capture-hold.ts`
  // sounds them as a capture's set — the remainder — starts its slide.
  | "capture"
  | "sur"
  | "surCancel"
  | "scoop"
  // ui / notifications
  | "tap"
  | "msgSend"
  | "msgReceive"
  | "notify"
  // BOARD-GAME cues. Context matters: a card flick has no business in منچ or backgammon, and
  // a "trick" clip is a card-game idea. These are context-NEUTRAL names for board pieces and
  // dice, aliased onto clips we already ship — no new assets, no bundle cost.
  | "boardStart"
  | "diceRoll"
  | "piecePlace"
  | "pieceHit"
  | "tokenEnter"
  | "tokenHome";

/** Clip files under `/sfx/` (all mono MP3; Kenney CC0 — see docs in the repo). */
const FILES: Record<SfxName, string> = {
  play: "card-play.mp3",
  deal: "card-deal.mp3",
  shuffle: "shuffle.mp3",
  trickWin: "trick-win.mp3",
  trickLose: "trick-lose.mp3",
  win: "win.mp3",
  lose: "lose.mp3",
  heartsBroken: "hearts-broken.mp3",
  qspade: "qspade.mp3",
  // شلم's auction — aliases whose meaning fits a card table (R5): a bid is a CHIP laid on the felt
  // (Kenney Casino's chip-lay-1, the clip تخته نرد's checker and منچ's `piecePlace` share — the stake
  // raised); «پاس» is a card laid down (the deal swish بی‌دل plays as its passed cards arrive); the
  // «شلم» call is the card games' «the hand just turned» sting (بی‌دل's hearts-broken) — a moment,
  // at the event tier. Levels: GAIN below, measured.
  bid: "bg-checker-place.mp3",
  pass: "card-deal.mp3",
  shelemCall: "hearts-broken.mp3",
  // the کف's pickup: the four cards GATHERED to the حاکم — the riffle the deal plays, quieter (an
  // alias); the trump call: an ORIGINAL clip (the card's snap and a small rising bell, E6 → A6),
  // synthesized by the committed `tools/sfx-probes/synth-trump-call.cjs` — not hearts-broken, which
  // `shelemCall` already is (the call and the trump can fall in one hand).
  widowPickup: "shuffle.mp3",
  trumpCall: "trump-call.wav",
  // the hand's end, once its 12th trick is collected: three ORIGINAL clips from the committed
  // `tools/sfx-probes/synth-shelem-hand.cjs` (trump-call's own voice) — a rising G-major arpeggio
  // (made), a dull falling minor third over a thud (set / یاسا / a failed call), six card snaps
  // gathering faster then a rising C-major arpeggio to the octave (all twelve: «شلم!»)
  shelemMade: "shelem-made.wav",
  shelemSet: "shelem-set.wav",
  shelemSlam: "shelem-slam.wav",
  // Chess cues — bespoke self-made CC0 wooden/bell clips (see public/sfx/LICENSE.txt): a
  // warm "tock" for a move, a clackier knock for a capture, a bell "ting" for the check
  // alert, a double wooden tap for castling, a rising chime for a promotion, a low gong
  // when a game begins, and a dull muted thunk for an illegal tap.
  chessMove: "chess-move.wav",
  chessCapture: "chess-capture.wav",
  chessCheck: "chess-check.wav",
  chessCastle: "chess-castle.wav",
  chessPromote: "chess-promote.wav",
  chessStart: "chess-start.wav",
  chessIllegal: "chess-illegal.wav",
  // Backgammon cues — ALL of them تخته نرد's own (see public/sfx/LICENSE.txt).
  //
  // The DICE + CHIP clips are derived from Kenney's "Casino Audio" (real dice shakes and throws,
  // chip clacks): two dice tumbling then landing (`bg-dice-roll`), a bigger clatter for doubles
  // (`bg-doubles`), a short wooden checker clack (`bg-checker-place`), a sharper double-clack hit
  // (`bg-hit`). The rest are ORIGINAL, self-made: a checker sliding into the tray, a dull
  // bumped-checker thunk, and a cascade of checkers for a مارس.
  //
  // ⚠️ THE THREE THAT WERE BORROWED ARE GONE. `bgBearOff` played a Kenney *Interface* confirmation
  // chime, `bgIllegal` played CHESS's `chess-illegal.wav`, and `bgGammon` played the CARD games'
  // `win.mp3`. (`bg-bearoff.mp3` is still on disk — منچ's `tokenHome` alias below is its only
  // remaining user, and منچ's sound was not in scope to change.)
  bgDiceLand: "bg-dice-roll.mp3",
  bgDoubles: "bg-doubles.mp3",
  // ⚠️ `bgDiceLand` IS ALSO THE §5.1 OPENING-CONTEST THROW. There used to be two bespoke
  // single-die clips (`bg-die-you`/`bg-die-opp`, one per side) and they were the wrong
  // instrument — measured, 1957/1137 Hz against this clip's 3768 Hz and 7.4/4.2 dB louder than a
  // roll of TWO dice, i.e. «the opening dice don't sound like the game's dice». Both files are
  // DELETED; the contest now plays the game's own dice, unchanged. See `game/backgammon-sfx.ts`.
  bgCheckerPlace: "bg-checker-place.mp3",
  bgHit: "bg-hit.mp3",
  bgBearOff: "bg-bear-off.wav",
  bgIllegal: "bg-illegal.wav",
  bgGammon: "bg-mars.wav",
  // پاسور's cues — aliases whose meaning fits a capture game (R5): your side's capture is the chips
  // clinking toward you (حکم's and بی‌دل's trick-win); a سور, the card games' «the hand just turned»
  // sting (بی‌دل's broken hearts, شلم's call); a cancel, the card games' «a penalty landed» sting
  // (بی‌دل's Q♠); the scoop, cards sliding across the felt (the swish بی‌دل's passed cards arrive with).
  capture: "trick-win.mp3",
  sur: "hearts-broken.mp3",
  surCancel: "qspade.mp3",
  scoop: "card-deal.mp3",
  // ---- board-game aliases (see the type above) --------------------------------------------
  /** A low gong — "a game begins". Used when a BOARD game opens from the lobby. */
  boardStart: "chess-start.wav",
  /** Dice tumbling and landing. */
  diceRoll: "bg-dice-roll.mp3",
  /** A wooden piece set down on the board. */
  piecePlace: "bg-checker-place.mp3",
  /** The sharper double-clack of one piece knocking another off its square. */
  pieceHit: "bg-hit.mp3",
  /** A double wooden tap — a piece ENTERING the board from off it. */
  tokenEnter: "chess-castle.wav",
  /** A short positive chime — a piece completing its journey home. */
  tokenHome: "bg-bearoff.mp3",
  tap: "tap.mp3",
  msgSend: "msg-send.mp3",
  msgReceive: "msg-receive.mp3",
  notify: "notify.mp3",
};

/** Per-clip loudness trim (relative to the master volume). */
const GAIN: Partial<Record<SfxName, number>> = {
  tap: 0.5,
  deal: 0.7,
  play: 0.85,
  shuffle: 0.8,
  trickWin: 0.7,
  trickLose: 0.55,
  msgSend: 0.6,
  msgReceive: 0.7,
  notify: 0.8,
  heartsBroken: 0.7,
  qspade: 0.75,
  // MEASURED (2026-10-10, Chromium's own `decodeAudioData`, the loudest 50 ms window, dBFS — the
  // table is in public/sfx/LICENSE.txt): card-play plays at −20.64 × .85 = −22.05; the chip clip is
  // −18.33 → × .65 = −22.07; card-deal is −20.19 → × .8 = −22.13 — the auction sits WITH the cards.
  // The call keeps hearts-broken's own .7 (−6.89 → −9.99): the card games' event tier, a moment.
  bid: 0.65,
  pass: 0.8,
  shelemCall: 0.7,
  // MEASURED the same way (P1 Task 5, 2026-10-11): the riffle's window is −14.16 → × .4 = −22.12,
  // the card level again (a pickup is cards, not a moment); the trump call's own clip is −7.80 →
  // × .38 = −16.20, the deal's level (its shuffle plays −16.10): the hand's second opening moment.
  widowPickup: 0.4,
  trumpCall: 0.38,
  // MEASURED the same way (P1 Task 6, 2026-10-11): the hand's end sits at the CALL's event tier
  // (−9.99 — a moment, never the cards' −22): made −8.81 → × .87 = −10.02; set −7.23 → × .73 = −9.96;
  // slam −10.26 at its full 1 (0.27 dB under: no trim can raise a clip — it reaches the tier as made).
  shelemMade: 0.87,
  shelemSet: 0.73,
  shelemSlam: 1,
  chessMove: 0.85,
  chessCapture: 0.9,
  chessCheck: 0.8,
  chessCastle: 0.8,
  chessPromote: 0.85,
  chessStart: 0.75,
  chessIllegal: 0.5,
  bgDiceLand: 0.85,
  bgDoubles: 0.7,
  bgCheckerPlace: 0.85,
  bgHit: 0.95,
  bgBearOff: 0.85,
  bgIllegal: 0.5,
  bgGammon: 0.9,
  // پاسور — MEASURED (Chromium's own `decodeAudioData`, the loudest 50 ms window's RMS, dBFS; the table
  // and the files' md5s are in public/sfx/LICENSE.txt): a capture sits at the trick-result tier —
  // trick-win −22.34 × .7 → −25.44, exactly حکم's and بی‌دل's `trickWin`; a سور at the event tier —
  // hearts-broken −6.89 × .7 → −9.99, `heartsBroken`'s and شلم's call's own; its cancel trimmed to
  // the سور — qspade −3.17 × .46 → −9.92; the scoop at the card tier — card-deal −20.19 × .8 → −22.13
  // against card-play's −20.64 × .85 → −22.05.
  capture: 0.7,
  sur: 0.7,
  surCancel: 0.46,
  scoop: 0.8,
};

/** Test seams: the clip each name plays, and its trim — so a test can pin an alias and the level
 *  arithmetic its gain was set from (never read by the app). */
export const FILES_FOR_TESTS: Readonly<Record<SfxName, string>> = FILES;
export const GAIN_FOR_TESTS: Readonly<Partial<Record<SfxName, number>>> = GAIN;

// UI/notification cues (they fire even when attention is away). Backgammon no longer has one:
// its «نوبت شماست» chime was removed, and it was the only backgammon cue in this category.
const UI_NAMES: ReadonlySet<SfxName> = new Set(["tap", "msgSend", "msgReceive", "notify"]);

const GAME_KEY = "bing:sound"; // pre-existing key — players keep their choice
const UI_KEY = "bing:sound-ui";

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) !== "0"; // default ON
  } catch {
    return true;
  }
}

let gameOn = readFlag(GAME_KEY);
let uiOn = readFlag(UI_KEY);

/** GAME sounds (cards, deals, turns, results). */
export function isSoundOn(): boolean {
  return gameOn;
}
export function setSoundOn(value: boolean): void {
  gameOn = value;
  try {
    localStorage.setItem(GAME_KEY, value ? "1" : "0");
  } catch {
    /* private mode */
  }
}

/** UI + notification sounds (taps, chat messages, alerts). */
export function isUiSoundOn(): boolean {
  return uiOn;
}
export function setUiSoundOn(value: boolean): void {
  uiOn = value;
  try {
    localStorage.setItem(UI_KEY, value ? "1" : "0");
  } catch {
    /* private mode */
  }
}

// ── Table mute (the OWNER's per-table «بی‌صدا کردن میز») ─────────────────────
// A transient, session-only override the online shell raises while the player is
// seated at a table whose owner muted it: EVERY effect is suppressed (game + chat)
// for the whole time they're on that table, regardless of their own toggles. Never
// persisted — cleared when they leave the table (the shell resets it on unmount).
let tableMuted = false;

/** Set by the online shell from the lobby `settings.muted`. `true` ⇒ all sound
 *  off while at this table; the shell MUST reset it to `false` on leave. */
export function setTableMuted(value: boolean): void {
  tableMuted = value;
}

/** Test/inspection seam. */
export function isTableMuted(): boolean {
  return tableMuted;
}

// ── The lazy Howler layer ────────────────────────────────────────────────────

interface HowlLike {
  play(): unknown;
  volume(v: number): unknown;
}
type HowlFactory = (src: string, volume: number) => HowlLike;

/** Injected fake for unit tests (never touches Howler/network). */
let factoryOverride: HowlFactory | null = null;
/** Test seam for the audio-context gate ({@link audioReady}) — the 2026-07-31 root cause.
 *  `howlerState` seeds Howler's OWN state field (see {@link markHowlerRunning}). */
export function __setAudioContextForTests(
  ctx: { state?: string; resume?: () => Promise<void> } | null,
  howlerState?: string,
): void {
  howlerGlobal =
    ctx === null ? null : { ctx, ...(howlerState !== undefined ? { state: howlerState } : {}) };
}

/** Test seam: Howler's own view of the context state. */
export function __howlerStateForTests(): string | undefined {
  return howlerGlobal?.state;
}

export function __setSfxFactoryForTests(factory: HowlFactory | null): void {
  cache.clear();
  howlerLoad = null;
  factoryOverride = factory;
}

const cache = new Map<SfxName, HowlLike>();
let howlerLoad: Promise<HowlFactory | null> | null = null;

/**
 * The Howler global (captured on load) so we can nudge its WebAudio context awake.
 *
 * `state` is HOWLER'S OWN view of the context — a separate field it seeds from `ctx.state` at
 * setup and thereafter only corrects inside its private `_autoResume`. It matters here because
 * `Howl.play()` gates on it (see {@link markHowlerRunning}).
 */
let howlerGlobal: {
  ctx?: { state?: string; resume?: () => Promise<void> } | null;
  state?: string;
} | null = null;

/**
 * Is the app ON SCREEN? Supplied by the caller (`main.tsx` wires the lifecycle's FUSED signal:
 * visibilitychange + pageshow/pagehide + freeze/resume + Telegram's own `miniApp.isActive`).
 *
 * Deliberately a seam rather than an import of `lifecycle`: the sound layer stays dependency-free
 * and testable, and the DEFAULT (`document.visibilityState`) is already correct in a plain
 * browser — the fused signal only matters inside the Telegram WebView, which is exactly where
 * this is wired.
 */
let appActiveSource: () => boolean = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

/** Wire the fused app-activity signal (see {@link appActiveSource}). */
export function setSfxActivitySource(source: () => boolean): void {
  appActiveSource = source;
}

function appActive(): boolean {
  try {
    return appActiveSource();
  } catch {
    return true; // never let an activity-probe failure silence the game
  }
}

/**
 * Keep HOWLER'S OWN view of the context in step with the real one.
 *
 * Howler tracks the context state in `Howler.state`, seeded from `ctx.state` at setup and
 * thereafter corrected only inside its private `_autoResume`. `Howl.play()` gates on it:
 *
 *     if (Howler.state === 'running' && Howler.ctx.state !== 'interrupted') playWebAudio();
 *     else { this._playLock = true; this.once('resume', playWebAudio); }
 *
 * So a raw `ctx.resume()` that leaves `Howler.state` stale at 'suspended' PARKS every later sound
 * on a `'resume'` event that only `_autoResume` ever emits — silent with a perfectly healthy
 * context. Correcting it here also removes a live unhandled-rejection surface: `_autoResume`'s own
 * `ctx.resume().then(…)` has NO `.catch()`, and it is reached from `play()` whenever Howler
 * believes the context is down. Nothing from the audio layer may reach the crash router.
 *
 * Only ever asserts what the REAL context already reports, and is fully guarded.
 */
function markHowlerRunning(): void {
  try {
    const g = howlerGlobal;
    if (!g || g.ctx?.state !== "running") return;
    if (g.state !== "running") g.state = "running";
  } catch {
    /* a Howler build without the field — the raw resume is what matters */
  }
}

/**
 * Wake the WebAudio context whenever it is not running (a no-op when it already is, when audio
 * isn't loaded, and when the context is CLOSED — that one is unrecoverable and resuming it only
 * produces a rejection).
 *
 * ⚠️ «SUSPENDED» IS NOT THE STATE A SCREEN LOCK PRODUCES ON iOS. WebKit — including the Telegram
 * WKWebView — moves the context to **`interrupted`**, its own extra `AudioContext.state` value;
 * Howler's source special-cases exactly that string in two places. This used to test
 * `state === "suspended"` and therefore did NOTHING after a lock, while `audioReady()` went on
 * refusing to play into a non-running context: every cue skipped, every tap a no-op, silent for
 * the rest of the session («lock the phone once, come back, and every game is silent»). Its own
 * docstring already said «suspended/interrupted/closed»; only the code disagreed.
 *
 * `resume()` mostly needs a user gesture, so this is called on every pointerdown (below) AND on
 * the app coming back on screen ({@link resumeAudioOnForeground}) — an interruption that has
 * ENDED can usually be resumed without waiting for a tap.
 */
export function resumeAudio(): void {
  try {
    const ctx = howlerGlobal?.ctx;
    if (!ctx) return;
    if (ctx.state === "running") {
      markHowlerRunning(); // nothing to wake — but Howler's own view may still be stale
      return;
    }
    if (ctx.state === "closed" || typeof ctx.resume !== "function") return;
    // `.then(…, onRejected)` covers the REJECTION; the surrounding try covers a synchronous
    // THROW — iOS raises `InvalidStateError: Failed to start the audio device` either way once
    // the OS has torn the context down behind a backgrounded WebView. Neither may escape: an
    // unhandled audio rejection is what the global crash router once painted as a fake
    // «اتصال برقرار نشد» over a live game.
    const pending = ctx.resume();
    if (pending && typeof pending.then === "function") {
      void pending.then(
        () => markHowlerRunning(),
        () => undefined,
      );
    } else {
      markHowlerRunning();
    }
  } catch {
    /* audio is decoration; never let waking it up become an app-level fault */
  }
}

/**
 * Wake the audio the moment the app comes back ON SCREEN, rather than waiting for the next tap.
 *
 * After a screen lock the player often just watches (a bot's turn, a replay), so the first cues
 * back would otherwise be lost. Fully guarded — a missing/throwing lifecycle must never break boot.
 * Returns the unsubscribe.
 */
export function resumeAudioOnForeground(
  subscribe: (listener: (active: boolean) => void) => () => void,
): () => void {
  try {
    return subscribe((active) => {
      if (active) resumeAudio();
    });
  } catch {
    return () => undefined;
  }
}

/**
 * Is the audio context in a state where handing Howler a `play()` is safe?
 *
 * THE 2026-07-31 BUG, in one sentence: on iOS the OS tears the audio context down while the
 * app is backgrounded, and a `play()` issued into that context rejects ASYNCHRONOUSLY from
 * inside Howler — past `playSfx`'s synchronous try/catch — as an unhandled rejection. The
 * global crash router then painted it as «اتصال برقرار نشد» over a live game with an inert
 * retry button. The beacon caught it twice on real phones within minutes of shipping.
 *
 * So we never ask Howler to play into a context that is not RUNNING. We nudge it awake
 * (guarded) and skip this one cue — a missed sound effect costs nothing, and the next tap
 * plays normally once the gesture-driven resume has landed.
 */
function audioReady(): boolean {
  try {
    const ctx = howlerGlobal?.ctx;
    if (!ctx) return true; // no WebAudio context yet (HTML5 fallback / not loaded) — let Howler decide
    if (ctx.state === "running") return true;
    resumeAudio(); // suspended/interrupted/closed → wake it for NEXT time, stay silent now
    return false;
  } catch {
    return false;
  }
}

/** Load Howler ON DEMAND (first sound after the first gesture). Failures are
 *  permanent-silent — sound is decoration, never worth an error surface. */
function loadFactory(): Promise<HowlFactory | null> {
  if (factoryOverride) return Promise.resolve(factoryOverride);
  howlerLoad ??= import("howler")
    .then(({ Howl, Howler }) => {
      // iOS/Safari (incl. the Telegram WKWebView): Howler's default `autoSuspend` suspends the
      // WebAudio context after ~30s of silence, and iOS does NOT reliably resume it on the next
      // play() — so game sound goes DEAD after a lull and only a page RELOAD brings it back. Keep
      // the context alive; `resumeAudio()` (on gesture) covers the separate backgrounding case.
      try {
        (Howler as { autoSuspend?: boolean }).autoSuspend = false;
        howlerGlobal = Howler as typeof howlerGlobal;
      } catch {
        /* an edge Howler build without the global — ignore, the factory still works */
      }
      const factory: HowlFactory = (src, volume) =>
        new Howl({ src: [src], preload: true, volume }) as unknown as HowlLike;
      return factory;
    })
    .catch(() => null);
  return howlerLoad;
}

function playNow(name: SfxName, volume: number): void {
  const cached = cache.get(name);
  if (cached) {
    cached.volume(volume);
    // Howler queues a play() issued while the clip is still downloading or the
    // context is locked — exactly the reliability the old tones lacked.
    cached.play();
    return;
  }
  void loadFactory().then((factory) => {
    if (!factory) return;
    // Re-check: the clip downloads asynchronously, so the app may have been
    // backgrounded (and the context torn down) since the cue was requested.
    if (!audioReady()) return;
    try {
      const howl = factory(`/sfx/${FILES[name]}`, volume);
      cache.set(name, howl);
      howl.play();
    } catch {
      /* decode/env failure → stay silent */
    }
  });
}

/** Play a named effect. No-ops when its category is muted, the master volume is
 *  zero, or audio is unavailable — callers never re-check the toggles. */
export function playSfx(name: SfxName): void {
  if (typeof window === "undefined") return;
  if (tableMuted) return; // the owner muted this table — silence everything while seated here
  if (UI_NAMES.has(name) ? !uiOn : !gameOn) return;
  // BACKGROUNDED ⇒ DON'T EVEN ASK.
  //
  // Nobody can hear a cue while the app is off screen, and iOS refuses to start the audio DEVICE
  // there — a `play()` then rejects ASYNCHRONOUSLY from inside Howler, past every try/catch here,
  // as an unhandled rejection. `audioReady()` cannot catch it: it gates on `ctx.state`, and the
  // context reports `running` while the DEVICE is the thing that will not start
  // («InvalidStateError: Failed to start the audio device», 110 occurrences with appActive=false,
  // still firing after the context gate shipped). Not playing a sound nobody could hear is the
  // fix; the report was the symptom.
  if (!appActive()) return;
  const master = getVolume();
  if (master <= 0) return;
  if (!audioReady()) return; // torn-down/suspended context — see audioReady()
  try {
    playNow(name, Math.min(1, master * (GAIN[name] ?? 1)));
  } catch {
    /* never let a sound break gameplay */
  }
}

/** Warm the hottest clips right after the first gesture (one idle round-trip,
 *  ~15 KB total) so the first card play isn't late. Fire-and-forget. */
export function warmSfx(): void {
  if (typeof window === "undefined") return;
  if (!gameOn && !uiOn) return;
  void loadFactory().then((factory) => {
    if (!factory) return;
    for (const name of ["play", "deal", "tap"] as const) {
      if (cache.has(name)) continue;
      try {
        cache.set(name, factory(`/sfx/${FILES[name]}`, GAIN[name] ?? 1));
      } catch {
        /* ignore */
      }
    }
  });
}

// Preload on the first interaction (the same moment mobile audio unlocks), and on EVERY later tap
// nudge a suspended context back awake — iOS needs the resume to ride a user gesture.
if (typeof window !== "undefined") {
  let warmed = false;
  const onGesture = (): void => {
    if (!warmed) {
      warmed = true;
      warmSfx();
    }
    resumeAudio(); // cheap no-op unless the context was suspended (backgrounding / lock)
  };
  window.addEventListener("pointerdown", onGesture, { passive: true });
}
